import type { TenantNavEntry } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { useMemo } from "react";
import { areaLabel } from "./installation-words";
import { useInstallationVocabulary } from "./use-installations";

/**
 * Gives the project entry of the sidebar the workspace's own plural ("Set-ups", "Engagements") when its vocabulary names one. Pure so the rule
 * can be tested without a session; an entry the server did not send, or sent locked, is left alone.
 */
export function withInstallationsLabel(entries: readonly TenantNavEntry[], label: string | undefined): TenantNavEntry[] {
  if (label === undefined) return [...entries];
  return entries.map((entry) => (entry.kind === "link" && entry.id === "installations" && entry.locked !== true ? { ...entry, displayLabel: label } : entry));
}

/** Whether the sidebar carries a usable project entry (the vocabulary route needs the module, the plan and the permission the entry already proves). */
export function hasInstallationsEntry(entries: readonly TenantNavEntry[]): boolean {
  return entries.some((entry) => entry.kind === "link" && entry.id === "installations" && entry.locked !== true);
}

export function useInstallationsNavLabel(entries: readonly TenantNavEntry[]): TenantNavEntry[] {
  const { i18n } = useTranslation();
  const present = hasInstallationsEntry(entries);
  const vocabulary = useInstallationVocabulary(present);
  const label = present ? areaLabel(vocabulary.data, i18n.language) : undefined;
  return useMemo(() => withInstallationsLabel(entries, label), [entries, label]);
}
